import { useSearchParams } from 'react-router-dom';
import { ORDER_ID_STORAGE_KEY } from '../order-board/catalog.js';
import { HANDOVER_MATCH_STORAGE_KEY, PICK_ITEM_ID_STORAGE_KEY } from './catalog.js';

export function useOrderIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('orderId') ?? params.get('id') ?? getStoredOrderId();
}

/** URL-only order id — avoids stale localStorage breaking context-free screens. */
export function useOrderIdFromQuery(): string | null {
  const [params] = useSearchParams();
  return params.get('orderId') ?? params.get('id');
}

export function usePickItemIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('itemId') ?? getStoredPickItemId();
}

export function getStoredOrderId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ORDER_ID_STORAGE_KEY);
}

export function setStoredOrderId(orderId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ORDER_ID_STORAGE_KEY, orderId);
}

export function getStoredPickItemId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(PICK_ITEM_ID_STORAGE_KEY);
}

export function setStoredPickItemId(itemId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PICK_ITEM_ID_STORAGE_KEY, itemId);
}

export function handoverMatchStorageKey(orderId: string): string {
  return `${HANDOVER_MATCH_STORAGE_KEY}.${orderId}`;
}

export function getHandoverCodeMatched(orderId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(handoverMatchStorageKey(orderId)) === '1';
}

export function setHandoverCodeMatched(orderId: string, matched: boolean): void {
  if (typeof sessionStorage === 'undefined') return;
  const key = handoverMatchStorageKey(orderId);
  if (matched) sessionStorage.setItem(key, '1');
  else sessionStorage.removeItem(key);
}

export function kitchenDisplayPath(): string {
  return '/kitchen-display';
}

export function kitchenTicketDetailPath(orderId: string): string {
  return `/kitchen-ticket-detail?orderId=${encodeURIComponent(orderId)}`;
}

export function kitchenSettingsPath(): string {
  return '/kitchen-settings';
}

export function pickListPath(orderId: string): string {
  return `/pick-list?orderId=${encodeURIComponent(orderId)}`;
}

export function pickItemPath(orderId: string, itemId: string): string {
  return `/pick-item?orderId=${encodeURIComponent(orderId)}&itemId=${encodeURIComponent(itemId)}`;
}

export function proposeSubstitutionPath(orderId: string, itemId?: string): string {
  const base = `/propose-substitution?orderId=${encodeURIComponent(orderId)}`;
  return itemId ? `${base}&itemId=${encodeURIComponent(itemId)}` : base;
}

export function collectionCounterPath(): string {
  return '/collection-counter';
}

export function handoverPath(orderId: string): string {
  return `/handover?orderId=${encodeURIComponent(orderId)}`;
}

export function ageVerificationPath(orderId: string): string {
  return `/age-verification?orderId=${encodeURIComponent(orderId)}`;
}

export function weighItemPath(orderId: string, itemId?: string): string {
  const base = `/weigh-item?orderId=${encodeURIComponent(orderId)}`;
  return itemId ? `${base}&itemId=${encodeURIComponent(itemId)}` : base;
}

export function weightExceptionsPath(orderId: string): string {
  return `/weight-exceptions?orderId=${encodeURIComponent(orderId)}`;
}

export function pickCompletePath(orderId: string, exceptions = false): string {
  const base = `/pick-complete?orderId=${encodeURIComponent(orderId)}`;
  return exceptions ? `${base}&exceptions=1` : base;
}
